import React from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Button,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import AnalyticsChart from "../customChart/AnalaticsChart";

const ExamCenterDetailsTable = ({
  rows,
  columns,
  loading,
  isAdmin,
  cities,
  selectedCity,
  setSelectedCity,
  selectedEnrollment,
  setSelectedEnrollment,
  selectedAnalytics,
  selectedAttendance,
  setSelectedAttendance,
}) => {
  const rowsWithIds = rows.map((row, index) => ({
    id: row.id || index,
    ...row,
  }));

  // Function to export data to Excel
  const exportToExcel = () => {
    const rowsToExport = rows.map((row) => ({
      ...row,
      centerStatus: row.centerStatus ? "Warning" : "Active",
    }));
    const worksheet = XLSX.utils.json_to_sheet(rowsToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Exam Center Details");
    XLSX.writeFile(workbook, "Exam_Center_Details.xlsx");
  };

  // Function to export data to PDF
  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text("Exam Center Details", 14, 15);

    const tableColumn = columns.map((col) => col.headerName);
    const tableRows = rows.map((row) =>
      columns.map((col) => {
        if (col.field === "centerStatus") {
          return row.centerStatus ? "Warning" : "Active";
        }
        return row[col.field] !== undefined ? row[col.field] : "N/A";
      })
    );

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 20,
    });

    doc.save("Exam_Center_Details.pdf");
  };

  return (
    <Box
      sx={{
        padding: "20px",
        backgroundColor: "#f7f9fc",
        borderRadius: "8px",
        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
      }}
    >
      {/* Filter and Export Button Container */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          mb: 2,
        }}
      >
        {/* Filter Controls */}
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          {/* City Filter */}
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Select City</InputLabel>
            <Select
              value={selectedCity}
              label="Select City"
              onChange={(e) => setSelectedCity(e.target.value)}
            >
              {cities.map((city) => (
                <MenuItem key={city} value={city}>
                  {city}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Enrollment Status Filter */}
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Enrollment Status</InputLabel>
            <Select
              value={selectedEnrollment}
              label="Enrollment Status"
              onChange={(e) => setSelectedEnrollment(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="started">Enrollment Started</MenuItem>
              <MenuItem value="not_started">Enrollment Not Started</MenuItem>
            </Select>
          </FormControl>
          {/* ✅ Add the new Attendance Status Filter dropdown */}
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Attendance Status</InputLabel>
            <Select
              value={selectedAttendance}
              label="Attendance Status"
              onChange={(e) => setSelectedAttendance(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="started">Attendance Started</MenuItem>
              <MenuItem value="not_started">Attendance Not Started</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Export Buttons (Visible only for Admin) */}
        {isAdmin && (
          <Box sx={{ display: "flex", gap: 2, mt: { xs: 2, md: 0 } }}>
            <Button onClick={exportToExcel} variant="contained" color="primary">
              Export to Excel
            </Button>
            <Button
              onClick={exportToPDF}
              variant="contained"
              style={{ backgroundColor: "#cd9621" }}
            >
              Export to PDF
            </Button>
          </Box>
        )}
      </Box>

      {/* Data Grid */}
      <Box sx={{ height: 600, width: "100%" }}>
        <DataGrid
          rows={rowsWithIds}
          columns={columns}
          loading={loading}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10,
              },
            },
          }}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
        />
      </Box>

      {/* Analytics Chart */}
      {selectedAnalytics && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Analytics for: {selectedAnalytics.centerName} (
            {selectedAnalytics.centerCode})
          </Typography>
          <AnalyticsChart data={selectedAnalytics} />
        </Box>
      )}
    </Box>
  );
};

export default ExamCenterDetailsTable;